import api from './axiosApi'
import type { PaginatedPlansResponse } from '@/types'
import type { PlanShareRead, PlanSharesResponse } from './shares'

// --- Filter option constants ---

export const ADMIN_PLAN_STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'SUBMITTED', label: 'Submitted' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'SENT_BACK', label: 'Sent Back' },
] as const

// --- Ownership transfer types ---

export interface OwnershipTransferResult {
  id: string
  from_owner: { id: string; email: string | null; display_name: string | null }
  to_owner: { id: string; email: string | null; display_name: string | null }
  transferred_at: string
  kept_as_editor: boolean
}

// --- API functions ---

interface ListAdminPlansParams {
  search?: string
  plan_type?: string
  status?: string
  owner_id?: string
  sort?: string
  offset?: number
  limit?: number
}

export async function listAdminPlans(
  params: ListAdminPlansParams = {}
): Promise<PaginatedPlansResponse> {
  const {
    search,
    plan_type,
    status,
    owner_id,
    sort,
    offset = 0,
    limit = 20,
  } = params

  const response = await api.get<PaginatedPlansResponse>('/admin/plans', {
    params: {
      q: search || undefined,
      plan_type: plan_type || undefined,
      status: status || undefined,
      owner_id: owner_id || undefined,
      sort: sort || undefined,
      offset,
      limit,
    },
  })

  return response.data
}

export async function deleteAdminPlan(
  planId: string,
  force = false
): Promise<void> {
  await api.delete(`/admin/plans/${planId}`, {
    params: { force },
  })
}

export async function transferPlanOwnership(
  planId: string,
  newOwnerId: string,
  keepAsEditor = true
): Promise<OwnershipTransferResult> {
  const response = await api.post<OwnershipTransferResult>(
    `/admin/plans/${planId}/ownership-transfers`,
    {
      new_owner_id: newOwnerId,
      keep_as_editor: keepAsEditor,
    }
  )
  return response.data
}

export async function getAdminPlanShares(
  planId: string
): Promise<PlanSharesResponse> {
  const response = await api.get<PlanSharesResponse>(
    `/admin/plans/${planId}/shares`
  )
  return response.data
}

export async function createAdminPlanShare(
  planId: string,
  userId: string
): Promise<PlanShareRead> {
  const response = await api.post<PlanShareRead>(
    `/admin/plans/${planId}/shares`,
    { user_id: userId, permission: 'EDIT' }
  )
  return response.data
}

export async function deleteAdminPlanShare(
  planId: string,
  shareId: string
): Promise<void> {
  await api.delete(`/admin/plans/${planId}/shares/${shareId}`)
}
